import type { StoreApi } from "zustand";

import { withPipelineConfigSync } from "../../../../common/stores/pipelineConfigSyncStore";
import {
  createAnalysisEntity,
  deleteAnalysisEntity,
  fetchAllAnalysisEntityEmails,
  fetchAnalysisEntities,
  updateAnalysisEntity,
  upsertAnalysisEntitiesBulk,
  upsertAnalysisEntity
} from "../../../../data/queries/accountAnalysis";
import { AnalysisEntity } from "../../types";
import { parseSupabaseError } from "../../utils/errorHandling";
import type { AccountAnalysisStore } from "../accountAnalysisStore";

export interface EntityActions {
  setEntities: (entities: AnalysisEntity[]) => void;
  setSelectedEntityIds: (ids: string[]) => void;
  toggleEntitySelection: (id: string) => void;
  clearEntitySelection: () => void;
  setIsLoadingEntities: (loading: boolean) => void;
  setEntitiesError: (error: string | null) => void;

  fetchEntities: () => Promise<void>;
  fetchAllEmails: () => Promise<{ email: string; is_deleted: boolean }[]>;
  addEntity: (entity: Omit<AnalysisEntity, "id" | "created" | "modified" | "deleted">) => Promise<void>;
  upsertEntity: (entity: Omit<AnalysisEntity, "id" | "created" | "modified" | "deleted">) => Promise<void>;
  upsertEntitiesBulk: (entities: Omit<AnalysisEntity, "id" | "created" | "modified" | "deleted">[]) => Promise<void>;
  updateEntity: (id: string, updates: Partial<AnalysisEntity>) => Promise<void>;
  deleteEntity: (id: string) => Promise<void>;
}

export const createEntitySlice = (
  set: StoreApi<AccountAnalysisStore>["setState"],
  get: StoreApi<AccountAnalysisStore>["getState"]
): EntityActions => {
  const synced = withPipelineConfigSync(get);

  return {
    setEntities: (entities) => set({ entities }),
    setSelectedEntityIds: (ids) => set({ selectedEntityIds: ids }),
    toggleEntitySelection: (id) =>
      set((state: AccountAnalysisStore) => ({
        selectedEntityIds: state.selectedEntityIds.includes(id)
          ? state.selectedEntityIds.filter((entityId) => entityId !== id)
          : [...state.selectedEntityIds, id]
      })),
    clearEntitySelection: () => set({ selectedEntityIds: [] }),
    setIsLoadingEntities: (isLoadingEntities) => set({ isLoadingEntities }),
    setEntitiesError: (entitiesError) => set({ entitiesError }),

    fetchEntities: async () => {
      set({ isLoadingEntities: true, entitiesError: null });
      try {
        const entities = await fetchAnalysisEntities();
        set({ entities, isLoadingEntities: false });
      } catch (error) {
        set({
          entitiesError: error instanceof Error ? error.message : "Failed to fetch entities",
          isLoadingEntities: false
        });
      }
    },

    addEntity: synced(async (entityData) => {
      try {
        const entity = await createAnalysisEntity(entityData);
        set((state: AccountAnalysisStore) => ({ entities: [entity, ...state.entities] }));
      } catch (error) {
        const errorMessage = parseSupabaseError(error);
        set({ entitiesError: errorMessage });
        throw error;
      }
    }),

    updateEntity: synced(async (id, updates) => {
      try {
        const updatedEntity = await updateAnalysisEntity(id, updates);
        set((state: AccountAnalysisStore) => ({
          entities: state.entities.map((e) =>
            e.id === id ? updatedEntity : e
          )
        }));
      } catch (error) {
        const errorMessage = parseSupabaseError(error);
        set({ entitiesError: errorMessage });
        throw error;
      }
    }),

    deleteEntity: synced(async (id) => {
      try {
        await deleteAnalysisEntity(id);
        set((state: AccountAnalysisStore) => ({
          entities: state.entities.filter((e) => e.id !== id)
        }));
      } catch (error) {
        const errorMessage = parseSupabaseError(error);
        set({ entitiesError: errorMessage });
        throw error;
      }
    }),

    fetchAllEmails: async () => {
      try {
        return await fetchAllAnalysisEntityEmails();
      } catch (error) {
        const errorMessage = parseSupabaseError(error);
        set({ entitiesError: errorMessage });
        throw error;
      }
    },

    upsertEntity: synced(async (entityData) => {
      try {
        const entity = await upsertAnalysisEntity(entityData);
        set((state: AccountAnalysisStore) => {
          const existingIndex = state.entities.findIndex((e) => e.email === entity.email);
          if (existingIndex >= 0) {
            return {
              entities: state.entities.map((e, index) =>
                index === existingIndex ? entity : e
              )
            };
          }
          return { entities: [entity, ...state.entities] };
        });
      } catch (error) {
        const errorMessage = parseSupabaseError(error);
        set({ entitiesError: errorMessage });
        throw error;
      }
    }),

    upsertEntitiesBulk: synced(async (entitiesData) => {
      try {
        const upsertedEntities = await upsertAnalysisEntitiesBulk(entitiesData);
        set((state: AccountAnalysisStore) => {
          const updatedEntities = [...state.entities];

          upsertedEntities.forEach(newEntity => {
            const existingIndex = updatedEntities.findIndex((e) => e.email === newEntity.email);
            if (existingIndex >= 0) {
              updatedEntities[existingIndex] = newEntity;
            } else {
              updatedEntities.unshift(newEntity);
            }
          });

          return { entities: updatedEntities };
        });
      } catch (error) {
        const errorMessage = parseSupabaseError(error);
        set({ entitiesError: errorMessage });
        throw error;
      }
    })
  };
};
